<template>
    <header 
        class="site-header skeleton-header" 
        :class="[
            theme ? `theme-${theme}` : '',
            pageClass,
            {
                'sticky': isSticky,
                'frosty': isSticky && !panelsOpen
            }
        ]"
    >
       <div class="site-header-container">
            <!-- Logo skeleton -->
            <div class="logo skeleton-element">
                <div class="skeleton-logo"></div>
            </div>
            
            <!-- Navigation skeleton -->
            <div class="navigation-wrapper">
                <nav class="navigation-skeleton">
                    <div class="nav-item skeleton-element"></div>
                    <div class="nav-item skeleton-element"></div>
                    <div class="nav-item skeleton-element"></div>
                    <div class="nav-item skeleton-element"></div>
                </nav>
            </div>
       </div>
    </header>
</template>

<script>
import { ref, onMounted, onBeforeUnmount, computed } from 'vue'
import { useRoute } from 'vue-router'
import { useEventListener } from '@vueuse/core'

export default {
    props: {
        theme: {
            type: String,
            default: 'xw-white'
        },
        panelsOpen: {
            type: Boolean,
            default: false
        },
        activePanel: {
            type: String,
            default: null
        }
    },
    setup(props) {
        const route = useRoute()
        const isSticky = ref(false)
        
        const handleScroll = () => {
            isSticky.value = window.scrollY > 100
        }
        
        onMounted(() => {
            handleScroll()
        })
        
        // Use VueUse for automatic event listener cleanup
        useEventListener('scroll', handleScroll, { passive: true })
        
        onBeforeUnmount(() => {
            // VueUse handles cleanup automatically
        })
        
        const pageClass = computed(() => {
            const currentPath = route.path
            
            // Panel pages (when panels are open)
            if (props.panelsOpen && props.activePanel) {
                if (props.activePanel === 'left') {
                    return 'header-design'
                }
                if (props.activePanel === 'right') {
                    return 'header-technology'
                }
            }
            
            // Direct panel URLs
            if (currentPath === '/design') {
                return 'header-design'
            }
            if (currentPath === '/technology') {
                return 'header-technology'
            }
            
            // Regular pages
            if (currentPath === '/') {
                return 'header-home'
            }
            if (currentPath === '/about') {
                return 'header-about'
            }
            if (currentPath === '/contact') {
                return 'header-contact'
            }
            if (currentPath === '/news') {
                return 'header-news-listing'
            }
            if (currentPath.startsWith('/news/')) {
                return 'header-news-single'
            }
            
            // Fallback
            return 'header-unknown'
        })
        
        return {
            isSticky,
            pageClass
        }
    }
}
</script>

<style lang="scss" scoped>
    @use '../../assets/scss/variables' as *;
    @use '../../assets/scss/container' as *;

    .skeleton-header {
         z-index: 1000;
         margin-top: 0px;
         position: fixed;
         width: 100%;
         transition: all 0.3s ease;

        &.sticky {
           top: 0;
           left: 0;
           right: 0;
           z-index: 1;
           
           .site-header-container {
               padding-top: 20px;
               padding-bottom: 5px;
               background: linear-gradient(to bottom, var(--theme-bg) 0%, var(--theme-bg) 100%, transparent 100%);
               backdrop-filter: blur(18px);
               -webkit-backdrop-filter: blur(18px);
           }
        }
    }
    
    .site-header-container{
        @extend %container;
        padding-top: 20px;
        position: relative;
    }
    
    .logo {
        position: absolute;
        left: 0;
        top: 65%;
        transform: translateY(-50%);
        z-index: 10;
        width: 180px;
        height: 30px; 
    }
    
    .skeleton-logo {
        width: 150px;
        height: 25px;
        border-radius: 4px;
    }
    
    .navigation-wrapper {
        width: 100%;
        display: flex;
        justify-content: center;
    }

    .navigation-skeleton {
        display: flex;
        gap: $spacing-xl;
        align-items: center;
        
        @media (max-width: $breakpoint-md) {
            gap: $spacing-lg;
        }
    }

    .nav-item {
        width: 80px;
        height: 20px;
        border-radius: 4px;
        
        &:last-child {
            width: 100px; // Slightly wider for variety
        }
        
        @media (max-width: $breakpoint-md) {
            width: 60px;
            height: 18px;
        }
    }

    // Skeleton animation styles
    .skeleton-element {
        background: linear-gradient(90deg, 
            rgba(0, 0, 0, 0.08) 25%, 
            rgba(0, 0, 0, 0.04) 50%, 
            rgba(0, 0, 0, 0.08) 75%
        );
        background-size: 200% 100%;
        animation: shimmer 1.5s infinite ease-in-out;
    }

    @keyframes shimmer {
        0% {
            background-position: -200% 0;
        }
        100% {
            background-position: 200% 0;
        }
    }

    // Theme-specific skeleton colors
    .theme-xw-white .skeleton-element,
    .theme-xw-yellow .skeleton-element,
    .theme-xw-grey .skeleton-element {
        background: linear-gradient(90deg, 
            rgba(0, 0, 0, 0.08) 25%, 
            rgba(0, 0, 0, 0.04) 50%, 
            rgba(0, 0, 0, 0.08) 75%
        );
    }

    .theme-xw-teal .skeleton-element {
        background: linear-gradient(90deg, 
            rgba(255, 255, 255, 0.15) 25%, 
            rgba(255, 255, 255, 0.08) 50%, 
            rgba(255, 255, 255, 0.15) 75%
        );
    }
</style> 